export type PetOriginVisualSection = 'worlds' | 'special';

export interface PetOriginBiomeVisual {
  /** Klucz grafiki z folderu `environment/` danego biomu. */
  textureKey: string;
  /** Zastępczy znak pokazywany wyłącznie, gdy asset nie może się załadować. */
  fallback: string;
  /** Większość naklejek mieści się w kafelku; tekstura mapy wypełnia go kadrem. */
  fit?: 'contain' | 'cover';
  /** Biomy edukacyjne/specjalne dostają własną sekcję w pickerze. */
  section?: PetOriginVisualSection;
  /** Dodatkowy wizualny znacznik nakładany na ikonę. */
  badge?: 'europe';
}

/**
 * Jedno źródło ikon biomów używanych przez wizualny picker kolekcji.
 *
 * Celowo korzystamy z istniejących assetów świata zamiast rysować uproszczone
 * odpowiedniki w CSS. Dziecko widzi w filtrze ten sam symbol, który spotyka na
 * mapie. Gdy nowego biomu zabraknie w tej mapie, picker użyje jego tekstury tła
 * zamiast mylącej ikony Łąki.
 */
export const PET_ORIGIN_BIOME_VISUALS: Readonly<Record<string, PetOriginBiomeVisual>> = {
  'biome-meadow': {
    textureKey: 'deco_flower_pink',
    fallback: 'Ł',
  },
  'biome-mountain': {
    textureKey: 'deco_ice_peak_cluster',
    fallback: 'G',
  },
  'biome-starwars': {
    textureKey: 'deco_starwars_cactus_funaja',
    fallback: 'DZ',
  },
  'biome-pixel': {
    textureKey: 'deco_pixel_block',
    fallback: 'PX',
  },
  'biome-ocean': {
    textureKey: 'deco_ocean_shell',
    fallback: 'O',
  },
  'biome-food': {
    textureKey: 'deco_food_fries_box',
    fallback: 'FF',
  },
  'biome-atlantis': {
    textureKey: 'deco_atlantis_poseidon_statue',
    fallback: 'A',
  },
  'biome-school': {
    textureKey: 'deco_school_apple',
    fallback: 'S',
  },
  'biome-arctic': {
    textureKey: 'deco_arctic_igloo',
    fallback: 'AR',
  },
  'biome-puszcza': {
    // Dąb — sylwetka, po której dziecko pozna Puszczę bez czytania nazwy.
    textureKey: 'deco_puszcza_oak',
    fallback: 'PN',
    section: 'special',
  },
  'biome-flags-europe': {
    textureKey: 'bg_flags_europe_tile',
    fallback: 'EU',
    fit: 'cover',
    section: 'special',
    badge: 'europe',
  },
  'biome-event-capibara-pumpkin-patch': { textureKey: 'deco_halloween_pumpkin_gate', fallback: 'H', section: 'special' },
  'biome-event-capibara-beach': {
    textureKey: 'deco_beach_palm',
    fallback: 'P',
  },
};

export function getPetOriginBiomeVisual(biomeId: string): PetOriginBiomeVisual | undefined {
  return PET_ORIGIN_BIOME_VISUALS[biomeId];
}
